import type { SettingsSubviewProps } from './Settings.types';
import type {
  ExperimentalFeature,
  SwitchExperimentalFeature,
  TextFieldExperimentalFeature,
} from '../../utils/types';
import type { ReactElement } from 'react';

import { Button, Flex, IconButton, Separator, Switch, Text, TextField } from '@rango-dev/whale';
import { RotateCw } from 'lucide-react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { updateWidgetConfig } from '../../state/WidgetSlice';
import { EXPERIMENTAL_FEATURES_LIST } from '../../utils/Constants';
import {
  getExperimentalFeatures,
  isFeatureAvailable,
  updateExperimentalStatus,
} from '../../utils/generalFunctions';
import { normalizeWith } from '../../utils/generalFunctions/common';

import styles from './Settings.module.css';
import { SettingsSubviewHeader } from './Settings.SubviewHeader';

const RESET_ICON_SIZE = 16;
const SWITCH_RELOAD_DELAY = 500;

export function SettingsExperimentalFeatures(props: SettingsSubviewProps): ReactElement {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [experimentalFeatures, setExperimentalFeatures] = useState(getExperimentalFeatures());
  const widgetConfig = useAppSelector((state) => state.widget);

  const [textFieldFeaturesValues, setTextFieldFeaturesValues] = useState<
    Record<string, TextFieldExperimentalFeature>
  >(
    normalizeWith(
      experimentalFeatures.filter((feature) => feature.type === 'text-field'),
      'name',
    ),
  );

  const isResetButtonEnabled = EXPERIMENTAL_FEATURES_LIST.some((item, index) => {
    const experimentalFeature = experimentalFeatures[index];
    if (item.type === 'switch' && experimentalFeature.type === 'switch') {
      return item.enable !== experimentalFeature.enable;
    }
    if (item.type === 'text-field' && experimentalFeature.type === 'text-field') {
      return item.value !== experimentalFeature.value;
    }
    return false;
  });

  const updateTextFieldValue = (feature: TextFieldExperimentalFeature, value: string) =>
    setTextFieldFeaturesValues((prevState) => ({
      ...prevState,
      [feature.name]: {
        ...prevState[feature.name],
        value,
      },
    }));

  const updateFeature = (feature: ExperimentalFeature) => {
    const updateExperimentalValues = experimentalFeatures.map((exp) =>
      exp.name === feature.name ? feature : exp,
    );
    setExperimentalFeatures(updateExperimentalValues);
    if (feature.type === 'switch') {
      dispatch(
        updateWidgetConfig({
          routing: {
            ...widgetConfig.routing,
            [feature.name]: feature.enable ? 'enabled' : 'disabled',
          },
        }),
      );
    }

    updateExperimentalStatus(feature);
  };

  const applyTextFieldFeatureUpdate = (feature: TextFieldExperimentalFeature) => {
    updateFeature({
      ...feature,
      value: textFieldFeaturesValues[feature.name].value,
    });

    if (feature.needsReloadTheApp) {
      setTimeout(() => window.location.reload(), 0);
    }
  };

  const applyTextFieldFeaturesUpdate = () => {
    EXPERIMENTAL_FEATURES_LIST.forEach((item) => updateFeature(item));
    setTimeout(() => window.location.reload(), 0);
  };

  const applySwitchFieldFeatureUpdate = (feature: SwitchExperimentalFeature) => {
    updateFeature({
      ...feature,
      enable: !feature.enable,
    });

    setTimeout(() => window.location.reload(), SWITCH_RELOAD_DELAY);
  };

  const visibleFeatures = experimentalFeatures.filter((feature) =>
    isFeatureAvailable(feature.name),
  );

  return (
    <Flex direction="column" gap="3">
      <SettingsSubviewHeader
        title={t('Experimental Features')}
        backButtonId="settings-experimental-back-btn"
        onBack={props.onBack}
        action={
          <IconButton
            id="settings-experimental-reset-btn"
            aria-label={t('Reset')}
            variant="ghost"
            color="gray"
            radius="full"
            size="1"
            disabled={!isResetButtonEnabled}
            onClick={applyTextFieldFeaturesUpdate}>
            <RotateCw size={RESET_ICON_SIZE} />
          </IconButton>
        }
      />

      <Flex direction="column" gap="4">
        {visibleFeatures.map((feature, index) => {
          const featureName = feature.name;
          const inputId = `app-settings-${featureName}-input`;
          const labelId = `app-settings-${featureName}-label`;

          return (
            <Fragment key={featureName}>
              {index > 0 && <Separator size="4" className={styles.separator} />}

              {feature.type === 'text-field' && (
                <Flex align="end" gap="4" width="100%">
                  <Flex direction="column" gap="2" flexGrow="1" minWidth="0">
                    <Text as="label" size="2" weight="medium" htmlFor={inputId} align="left">
                      {t(feature.displayName)}
                    </Text>
                    <TextField.Root
                      id={inputId}
                      size="2"
                      value={textFieldFeaturesValues[featureName].value}
                      onChange={(event) => updateTextFieldValue(feature, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          applyTextFieldFeatureUpdate(feature);
                        }
                      }}
                    />
                  </Flex>
                  <Button
                    id={`app-settings-${featureName}-apply-btn`}
                    size="2"
                    radius="full"
                    color="cyan"
                    className={styles.applyButton}
                    onClick={() => applyTextFieldFeatureUpdate(feature)}>
                    {t('Apply')}
                  </Button>
                </Flex>
              )}

              {feature.type === 'switch' && (
                <Flex align="center" justify="between" gap="4" width="100%">
                  <Flex align="center" gap="1" flexGrow="1" minWidth="0">
                    <Text id={labelId} size="3" weight="medium" truncate>
                      {t(feature.displayName)}
                    </Text>
                  </Flex>
                  <Switch
                    id={`app-settings-${featureName}-switch`}
                    aria-labelledby={labelId}
                    size="1"
                    color="cyan"
                    variant="classic"
                    checked={feature.enable ?? false}
                    onCheckedChange={() => applySwitchFieldFeatureUpdate(feature)}
                  />
                </Flex>
              )}
            </Fragment>
          );
        })}
      </Flex>
    </Flex>
  );
}
